// controllers/chart_controller.js
//
// Shared controller for all chart variants.

import ApplicationController from './application_controller'
import Chart from 'chart.js/auto'

import { resolveDatasetColors } from './chart/theme'

import { SimpleBarVariant } from './chart/variants/simple_bar_variant'
import { SmoothLineVariant } from './chart/variants/smooth_line_variant'
import { GridOnlyVariant } from './chart/variants/grid_only_variant'
import { ProfileRingVariant } from './chart/variants/profile_ring_variant'
import { CategoryBreakdownVariant } from './chart/variants/category_breakdown_variant'

// Chart variant registry.
const VARIANTS = {
  simple_bar: SimpleBarVariant,
  smooth_line: SmoothLineVariant,
  grid_only: GridOnlyVariant,
  profile_ring: ProfileRingVariant,
  category_breakdown: CategoryBreakdownVariant,
}

// White background inside chart plot area.
const chartAreaBackground = {
  id: 'chartAreaBackground',

  beforeDraw({ ctx, chartArea }) {
    if (!chartArea) return

    ctx.save()

    ctx.fillStyle = '#FFFFFF'

    ctx.fillRect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top
    )

    ctx.restore()
  },
}

export default class extends ApplicationController {
  static values = {
    variant: String,
    data: Object,
    options: Object,
  }

  connect() {
    const variant = this.#resolveVariant()
    const data = this.dataValue
    const options = this.optionsValue || {}

    resolveDatasetColors(data.datasets || [])

    variant.applyDataDefaults(data.datasets || [])

    const chartOptions = variant.buildOptions(options)

    this.chart = new Chart(this.element, {
      type: variant.chartType,
      data,
      options: chartOptions,
      plugins: this.getPlugin(options),
    })
  }

  disconnect() {
    this.chart?.destroy()
  }

  getPlugin(options) {
    const plugins = [chartAreaBackground]
    if (this.variantValue === 'profile_ring' && options.score) {
      plugins.push(ProfileRingVariant.centerTextPlugin(options.score))
      return plugins
    }
    return plugins
  }

  #resolveVariant() {
    const variant = VARIANTS[this.variantValue]

    if (!variant) {
      throw new Error(
        `[chart_controller] Unknown variant "${this.variantValue}". ` +
        `Available variants: ${Object.keys(VARIANTS).join(', ')}`
      )
    }

    return variant
  }
}
